import {
  GvDatePickerController,
  GvInputController,
  GvRadioGroupController,
} from "../../../components/Controllers";
import { LIST_GENDERS, MEDICAL_RECORDS_OPTIONS, NEUTERED_SPAYED_OPTIONS } from "../../../config/params";
import { PetFormType, PetSchema } from "../../../services/pet-create/pet-create.schema";
import React, { FC, useEffect, useState } from "react";
import { getGendersWithIcons, getSpeciesWithIcons } from "./PetNewOneValues";

import { GvBreedSearchAutocomplete } from "../../../components";
import { IOptionGeneric } from "../../interface";
import { StyledBookingFlowButton } from "../../containers/BookingScheduleContainer/BookingScheduleContainer.style";
import { Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useGTM } from "../../../hooks/useGTM";
import { useSpecies } from "../../../services/specie/specie.service";
import { yupResolver } from "@hookform/resolvers/yup";

interface IPetNewOneFormProps {
  onSubmit: (data: PetFormType) => void;
  buttonText?: string;
}

const PetNewOneForm: FC<IPetNewOneFormProps> = ({ onSubmit, buttonText }) => {
  const { dispatchEvent } = useGTM();
  const [breedName, setBreedName] = useState<string>("");
  const [specieName, setSpecieName] = useState<string>("");
  const { loading: isLoadingSpecies, species } = useSpecies();

  const defaultPetValues: PetFormType = {
    name: "",
    species: "",
    breed: "",
    gender: "",
    isDesexed: "",
    anyMedicalRecord: "",
    medicalRecordCity: "",
    medicalRecordName: "",
    birthdate: null,
  };

  const { control, handleSubmit, watch, setValue } = useForm<PetFormType>({
    mode: "onSubmit",
    resolver: yupResolver(PetSchema),
    defaultValues: defaultPetValues,
  });

  const watchSpecies = watch("species", "");
  const watchAnyMedicalRecord = watch("anyMedicalRecord", "");
  const watchGender = watch("gender", "");
  const watchIsDesexed = watch("isDesexed", "");

  const handleChangeBreed = (text: string) => {
    if (!text) {
      setValue("breed", "", { shouldValidate: true });
      setBreedName("");
    }
  };

  const handleSelectBreed = (breed: IOptionGeneric) => {
    setValue("breed", breed.value, { shouldValidate: true });
    setBreedName(breed.label);
  };


  useEffect(() => {
    if (watchSpecies) {
      const specieNameTemp = species.find((e) => e.value === watchSpecies)?.label;

      setValue("breed", "");
      setBreedName("");
      setSpecieName(specieNameTemp);
      dispatchEvent({
        eventName: "gv_add_a_pet_type",
        eventProps: {
          petType: specieNameTemp,
        },
      });
    }
  }, [watchSpecies]);

  useEffect(() => {
    if (watchAnyMedicalRecord) {
      const medicalRecordValue = MEDICAL_RECORDS_OPTIONS.find((e) => watchAnyMedicalRecord)?.label;

      dispatchEvent({
        eventName: "gv_add_a_pet_medical_record",
        eventProps: {
          medicalRecordValue,
        },
      });
    }
  }, [watchAnyMedicalRecord]);

  useEffect(() => {
    if (watchGender) {
      const genderValue = LIST_GENDERS.find((e) => e.value === watchGender)?.label;

      dispatchEvent({
        eventName: "gv_add_a_pet_gender",
        eventProps: {
          genderValue,
        },
      });
    }
  }, [watchGender]);

  useEffect(() => {
    if (watchIsDesexed) {
      const neuteredValue = NEUTERED_SPAYED_OPTIONS.find((e) => e.value === watchIsDesexed)?.label;

      dispatchEvent({
        eventName: "gv_add_a_pet_neutered_spayed",
        eventProps: {
          neuteredValue,
        },
      });
    }
  }, [watchIsDesexed]);


  return (
    <form onSubmit={handleSubmit(onSubmit)}
      className="">
      <div className="flex flex-col justify-between">
        <div className="w-full ">
          <GvInputController
            control={control}
            name="name"
            label="What's the name of your pet?"
            className="font-semibold"
            placeholder="Add a name"
            labelSx={{ textTransform: "none" }}
          />
        </div>
        <div className="w-full font-semibold ">
          <GvDatePickerController
            control={control}
            name="birthdate"
            label="When is your pet’s birthday?"
            disableFuture={true}
            minDate={new Date(631152000 * 1000)}
            maxDate={new Date()}
          />
        </div>
      </div>
      <GvRadioGroupController
        defaultChecked={false}
        control={control}
        name="species"
        label="What type of pet do you have?"
        options={getSpeciesWithIcons(species)}
        variant="block"
        sx={{ display: "grid", gap: "1rem", gridTemplateColumns: "1fr 1fr 1fr" }}
        isLoading={isLoadingSpecies}
      />
      {specieName !== "Other" ? (
        <GvBreedSearchAutocomplete
          id="breedAutocomplete"
          name="breed"
          control={control}
          specieId={watchSpecies}
          onSelect={handleSelectBreed}
          onChange={handleChangeBreed}
          text={breedName}
          disabled={!watchSpecies}
          breedWarning={false}
        />
      ) : null}
      <GvRadioGroupController
        control={control}
        defaultChecked={false}
        name="gender"
        label="Gender"
        options={getGendersWithIcons(LIST_GENDERS)}
        variant="block"
        sx={{ display: "grid", gap: "1rem", gridTemplateColumns: "1fr 1fr" }}
      />
      <GvRadioGroupController
        control={control}
        defaultChecked={false}
        name="isDesexed"
        label="Is your pet neutered/spayed?"
        options={NEUTERED_SPAYED_OPTIONS}
        variant="block"
        sx={{ display: "grid", gap: "1rem", gridTemplateColumns: "1fr 1fr" }}
      />
      <GvRadioGroupController
        control={control}
        defaultChecked={false}
        name="anyMedicalRecord"
        label="Does your pet have a medical history?"
        options={MEDICAL_RECORDS_OPTIONS}
        variant="block"
        sx={{ display: "grid", gap: "1rem", gridTemplateColumns: "1fr 1fr" }}
      />
      {watchAnyMedicalRecord == "1" && (
        <>
          <Typography variant="h4" sx={{ mb: 2, fontFamily: "geograph-regular" }}>
            Who should we contact to get the most recent records for your pet?
          </Typography>
          <GvInputController
            control={control}
            name="medicalRecordCity"
            label="Clinic Address"
            placeholder="Street, City, State"
          />
          <GvInputController
            control={control}
            name="medicalRecordName"
            label="Veterinarian"
            placeholder="Name of your veterinarian"
          />
        </>
      )}
      <div className="flex items-center !bg-gray-2 !sticky -bottom-0.5 z-100">
        <StyledBookingFlowButton
          fullWidth
          type="submit"
          className="font-semibold"
        >
          {buttonText ? buttonText : "Done"}
        </StyledBookingFlowButton>
      </div>
    </form>
  );
};

export default PetNewOneForm;
